import { runAiQuality } from 'libs/aiQuality';
import { downloadTranslationDirect, shouldDisableSegmentsByTaskStatus } from 'libs/commonFuncs';
import * as gtag from 'libs/gtag';
import Notifications from 'libs/notifications';
import { SEGMENT_STATUS } from 'libs/utils';
import memoizeOne from 'memoize-one';
import ModelConcordance from 'models/concordance';
import ModelFiles from 'models/files';
import ModelQA from 'models/QA';
import ModelSegments from 'models/segments';
import ModelSpellCheck from 'models/spellCheck';
import ModelTasks from 'models/tasks';
import { getSegmentContext, getSegmentTask, getTaskSegmentIds, getEditorSegmentIds, getEditorScopeKey } from './taskContext';
import ModelTranslationMemories from 'models/translationMemories';
import { insertTextAtCursor, placeCaretAtEnd, getSourceCaretPosition, canSplitSourceAtPosition } from './commonFuncs';
import { getSuggestionSourceTaskID, getVisibleSuggestions } from './sidebar/top/suggestionUtils';
import _ from 'lodash';
import { debounceByParam } from '../../libs/debounceByParam';
import { batch } from 'react-redux';
import useCurrentUser from "../../libs/useCurrentUser";

const getQuill = async () => {
	if (typeof window === 'undefined') return null;

	const quillModule = await import('quill');
	return quillModule?.default ?? quillModule;
};

const getUniqueSegmentIds = (segmentIds = []) => {
	return [...new Set(segmentIds)];
};

const getTaskOptions = (state) => getUniqueSegmentIds(getEditorSegmentIds(state.traEditor));

let nextSegmentsRequestID = 0;
const pendingSegmentSaves = new Set();
const trackSegmentSave = (promise) => {
	pendingSegmentSaves.add(promise);
	promise.then(() => pendingSegmentSaves.delete(promise), () => pendingSegmentSaves.delete(promise));
	return promise;
};

const isGridRowVisible = function (ele) {
	if (!ele) return false;

	const container = document.querySelector('.ReactVirtualized__Grid');
	if (!container) return false;

	const { bottom, height, top } = ele.getBoundingClientRect();
	const containerRect = container.getBoundingClientRect();

	return top >= containerRect.top && bottom <= containerRect.bottom;
};

const ns = 'traEditor';

function nextTick(fn) {
	Promise.resolve().then(fn);
}

export const updateSegments = () => async (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};
	const arSegments = state.arSegments;
	const activeSegment = state?.activeSegment;
	let segments = [...arSegments.filter(i => i.sameAs < 0 || i.sameAs > 0 || i.isInitRepetition || i.isRepetition)].map(i => i[1]);;

	let response = await ModelSegments.getSegmentsOnRequest(segments);

	let freshSegments = response.map(i => {
		return {
			id: i.id,
            sameAs: i.sameAs,
			isInitRepetition: i.isInitRepetition,
			isRepetition: i.isRepetition,
			repetitionEnabled: i.repetitionEnabled,
			translationText: i.translationText
        };
	})

	dispatch({
		type: 'traEditor/updateSegments',
		payload: freshSegments,
	});

	if (activeSegment && activeSegment?.id) {
		let activeSegmentId = Number(activeSegment?.id);
		let actualSegment = null;

		for (const item of response) {
			if (activeSegmentId === Number(item.id)) {
				actualSegment = item;
				break;
			}
		}

		if (actualSegment) {
			dispatch({
				type: 'traEditor/updateSegment',
				payload: actualSegment,
			});
		}
	}
}

export const getSegments = () => (dispatch, getState) => {
	const state = getState().traEditor;
	const taskID = state.task?.id;
	const taskIds = state.joinedTaskFocus ? [state.joinedTaskFocus] : state.taskids?.length ? state.taskids : [taskID];
	const scopeKey = getEditorScopeKey(state);
	const requestID = ++nextSegmentsRequestID;
	const isCurrent = () => getState().traEditor.segmentsRequestID === requestID
		&& getEditorScopeKey(getState().traEditor) === scopeKey;
	dispatch({ type: 'traEditor/setProp', payload: { loading: true, segmentsRequestID: requestID } });
	return ModelSegments.getForTaskByIds(taskIds)
		.then((segments) => {
			if (!isCurrent()) return false;
			if (!Array.isArray(segments)) throw new Error('Unable to load task segments');
			const qaResultsByTask = getState().traEditor.qaResultsByTask ?? {};
			dispatch({ type: 'traEditor/initSegments', payload: segments.map((segment) => ({
				...segment,
				qaResults: qaResultsByTask[segment.task]?.[segment.id] ?? segment.qaResults ?? [],
			})) });
			dispatch({ type: 'traEditor/setProp', payload: { segmentsLoaded: true } });
			if (state.autoTranslating) {
				ModelTasks.getForEditor(taskID).then((task) => {
					if (isCurrent()) dispatch({ type: 'traEditor/setProp', payload: { autoTranslating: task?.isPretranslating == '1' } });
				});
			}
			return true;
		})
		.catch((error) => {
			if (isCurrent()) Notifications.error('Unable to load task segments. Please try again.');
			return false;
		})
		.finally(() => {
			if (isCurrent()) dispatch({ type: 'traEditor/setProp', payload: { loading: false } });
		});
};

// Updates segments with changes in comments or revision locks
export const getSegmentsSilent = () => async (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};

	const taskID = state?.task?.id ?? 0;
	console.log('taskIds', taskID);
	const oldSegments = state?.arSegments;
	const scopeKey = getEditorScopeKey(state);

	try {
		const segments = state.taskids?.length > 1
			? await ModelSegments.getForTaskByIds(state.joinedTaskFocus ? [state.joinedTaskFocus] : state.taskids)
			: await ModelSegments.getForTask({ id: taskID });

		if (getEditorScopeKey(getState().traEditor) !== scopeKey) return;
		segments?.forEach((segment) => {
			// Updates in comments
			const hasCommentChanges =
				segment?.commentsCount !=
				oldSegments.getSegment(segment?.id)?.commentsCount;

			// Updates in revision lock
			const hasRevisionChanges =
				segment?.isRevisionLocked !=
				oldSegments.getSegment(segment?.id)?.isRevisionLocked;

			if (hasCommentChanges || hasRevisionChanges) {
				dispatch({ type: 'traEditor/updateSegment', payload: {
					id: segment.id, commentsCount: segment.commentsCount, unresolvedCommentsCount: segment.unresolvedCommentsCount,
					resolvedCommentsCount: segment.resolvedCommentsCount, commentTypes: segment.commentTypes, isRevisionLocked: segment.isRevisionLocked,
					skipRepetitions: true,
				} });
			}
		});
	} catch (err) {
		console.error(err);
	}
};

export const getSelectedSegments = (state) => {
	return state.arDisplaySegments
		.filter(({ checked }) => checked)
		.toList()
		.toJS();
};

const segmentIsEditable = (S) => {
	return !(S.isLocked || S.isRevisionLocked || !S?.available);
};

const getPlainSegmentText = (text = '') =>
	String(text ?? '')
		.replace(/<[^>]*>/g, '')
		.replace(/&nbsp;/gi, ' ')
		.trim();

export const areAllTargetsEmpty = (segments) => {
	const segmentsList = segments?.toList ? segments.toList().toJS() : segments;

	return (
		Array.isArray(segmentsList) &&
		segmentsList.length > 0 &&
		segmentsList.every((S) => getPlainSegmentText(S?.translationText) === '')
	);
};

export const copySourceToTarget = () => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};
	const segments = getSelectedSegments(state);
	if (segments.length == 0) return;

	segments.forEach((S) => {
		if (!segmentIsEditable(S)) return;

		S.hasChanges = true;
		S.forSave = true;
		S.matchType = 'S2T';
		S.matchScore = '';
		S.nextStatus = 'draft';
		S.translationText = S.sourceText;
		dispatch({ type: 'traEditor/updateSegment', payload: S });
	});

	gtag.event({
		category: 'Editor Actions',
		action: 'Copy Source to Target',
		label: '',
	});

	nextTick(() => dispatch(saveAll()));
};

export const copySourceToAllEmptyTargets = () => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};
	const segments = state.arSegments?.toList ? state.arSegments.toList().toJS() : [];
	if (!areAllTargetsEmpty(segments)) return;

	const segmentsToUpdate = segments
		.filter((S) => segmentIsEditable(S))
		.map((S) => ({
			...S,
			hasChanges: true,
			forSave: true,
			matchType: 'S2T',
			matchScore: '',
			nextStatus: 'draft',
			translationText: S.sourceText,
		}));

	if (segmentsToUpdate.length == 0) return;

	segmentsToUpdate.forEach((S) => {
		dispatch({ type: 'traEditor/updateSegment', payload: S });
	});

	gtag.event({
		category: 'Editor Actions',
		action: 'Copy Source to All Empty Targets',
		label: '',
	});

	nextTick(() => dispatch(saveAll()));
};

export const clearTarget = () => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};
	const segments = getSelectedSegments(state);
	if (segments.length == 0) return;

	segments.forEach((S) => {
		if (!segmentIsEditable(S)) {
			return;
		}

		S.hasChanges = true;
		S.forSave = true;
		S.nextStatus = 'draft';
		S.status = 'draft';
		S.translationText = '';
		S.matchScore = '';
		S.matchType = '';
		dispatch({ type: 'traEditor/updateSegment', payload: S });
	});

	gtag.event({
		category: 'Editor Actions',
		action: 'Clear Target',
		label: '',
	});

	nextTick(() => dispatch(saveAll()));
};

export const saveAllSegments = () => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};
	const { task, taskids } = state;

	const arSegmentsToSave = state.arSegments
		.map((S) => {
			const sToSave = { ...S, ...getSegmentContext(state, S) };

			sToSave.sourceText = sToSave.sourceText.replace(/<\/?highlight.*?>/g, '');
			sToSave.translationText = sToSave.translationText.replace(
				/<\/?highlight.*?>/g,
				''
			);

			return sToSave;
		})
		.toList()
		.toJS();

	if (arSegmentsToSave.length == 0) return;

	dispatch({
		type: 'traEditor/setBatchSaving',
		payload: {
			show: true,
			msg: 'Saving Segments...',
		},
	});

	dispatch({ type: 'traEditor/updateSegments', payload: arSegmentsToSave });

	const pObj = {
		arSegmentsToSave,
		targetLang: task.targetLang,
		project: task.project.id,
		file: task.file.id,
		step: task.step,
		// task: task.id,
		stepOrdr: task.stepOrdr,
		taskIDs: state.joinedTaskFocus ? [state.joinedTaskFocus] : taskids
	};

	ModelSegments.saveAllSegments(pObj).then(r => {
		dispatch({
			type: 'traEditor/setBatchSaving',
			payload: {
				show: false
			},
		});
	})
}

export const saveAllSegmentsToElastic = () => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};
	const { task, taskids } = state;

	const arSegmentsToSave = state.arSegments
		.map((S) => {
			const sToSave = { ...S, ...getSegmentContext(state, S) };

			sToSave.sourceText = sToSave.sourceText.replace(/<\/?highlight.*?>/g, '');
			sToSave.translationText = sToSave.translationText.replace(
				/<\/?highlight.*?>/g,
				''
			);

			return sToSave;
		})
		.toList()
		.toJS();

	if (arSegmentsToSave.length == 0) return;

	const pObj = {
		arSegmentsToSave,
		targetLang: task.targetLang,
		project: task.project.id,
		file: task.file.id,
		step: task.step,
		// task: task.id,
		stepOrdr: task.stepOrdr,
		taskIDs: state.joinedTaskFocus ? [state.joinedTaskFocus] : taskids
	};

	ModelSegments.saveAllSegmentsToStorage(pObj).then(r => {
	})
}

export const confirmActiveSegmentWithoutSaving = () => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};
	const activeSegment = state?.activeSegment;

	if (!activeSegment) return;

	dispatch({
		type: 'traEditor/updateSegment',
		payload: {
			id: activeSegment?.id,
			hasChanges: false,
			forSave: true,
			status: 'confirmed',
			nextStatus: 'confirmed',
			isEdited: true,
			updateTM: true
		},
	});
}

export const confirm =
	(singleSegment = null, updateTM = null) =>
	(dispatch, getState) => {
		const state = getState()?.traEditor ?? {};
		const arSegments = state?.arSegments;
		const segments = singleSegment
			? [singleSegment]
			: getSelectedSegments(state);
		const activeSegment = state?.activeSegment;

		if (segments.length == 0) return;

		const user = getState()?.user;
		const isAutoPopulateRepetitions = !!user?.autoPopulateRepetitions;

		let firstSegment = singleSegment ?? segments[0];
		const propagatedTranslationText =
			singleSegment?.translationText ??
			activeSegment?.translationText ??
			firstSegment?.translationText ??
			'';
		let id=null;
		let sameAs = parseInt(firstSegment?.sameAs ?? 0);
		if (sameAs && sameAs > 0) {
			id = sameAs;
		} else {
			id = parseInt(firstSegment?.id);
		}

		let filtredSegments = arSegments.filter((item) => parseInt(item?.sameAs ?? 0) === id || parseInt(item?.id ?? 0) === id);

		filtredSegments = filtredSegments.filter((item) => !item?.isLocked);

		if (isAutoPopulateRepetitions) {
			for (const [key, item] of filtredSegments) {
				if (!segmentIsEditable(item) || item.status == SEGMENT_STATUS.CONFIRMED)
				{
					continue;
				}

				if (item?.id == activeSegment?.id) {
					continue;
				}

				const updatedSegment = {
					...item,
					id: key,
					hasChanges: false,
					forSave: true,
					status: 'confirmed',
					nextStatus: 'confirmed',
					translationText: propagatedTranslationText,
					updateTM,
					// saveAll() (dispatched a few lines below via nextTick) always
					// re-dispatches traEditor/updateSegments right away, which
					// recomputes areAllConfirmed authoritatively - so doing it here
					// too is a redundant full-array scan on every confirm. On a
					// large task (tens of thousands of segments) that's real,
					// avoidable main-thread work happening on every Ctrl+Enter.
					skipAreAllConfirmed: true,
				};

				dispatch({ type: 'traEditor/updateSegment', payload: updatedSegment });
			}
		}
		//just delay confirm after text update

		batch(() => {
			// TODO. Remove after upgrade to react 18
			segments.forEach((S) => {
				if (!segmentIsEditable(S) || (S.status == SEGMENT_STATUS.CONFIRMED && !S.hasChanges && !S.forSave))
					return;

				const updatedSegment = {
					...S,
					hasChanges: false,
					forSave: true,
					status: 'confirmed',
					nextStatus: 'confirmed',
					updateTM,
					// See comment above - saveAll() recomputes this right after.
					skipAreAllConfirmed: true,
				};

				dispatch({ type: 'traEditor/updateSegment', payload: updatedSegment });
			});
		});

		const confirmedSegmentsForQA = segments
			.filter((S) => segmentIsEditable(S) && (
				S.status != SEGMENT_STATUS.CONFIRMED ||
				S.hasChanges ||
				S.forSave
			))
			.map((S) => ({
				...S,
				hasChanges: false,
				forSave: true,
				status: 'confirmed',
				nextStatus: 'confirmed',
				updateTM,
			}));

		gtag.event({
			category: 'Editor Actions',
			action: 'Confirm',
			label: '',
		});

		nextTick(() => {
			const savePromise = dispatch(saveAll());

			if (savePromise?.then) {
				savePromise.then((saved) => {
					if (saved) {
						dispatch(recheckConfirmedSegmentsQA(confirmedSegmentsForQA));
					}
				});
			}
		});
	};

export const unConfirm =
	(singleSegment = null) =>
	(dispatch, getState) => {
		const state = getState()?.traEditor ?? {};
		const segments = singleSegment
			? [singleSegment]
			: getSelectedSegments(state);
		if (segments.length == 0) return;

		segments.forEach((S) => {
			if (!segmentIsEditable(S)) return;

			const updatedSegment = {
				...S,
				hasChanges: false,
				forSave: true,
				status: 'draft',
				nextStatus: 'draft',
			};

			dispatch({ type: 'traEditor/updateSegment', payload: updatedSegment });
		});

		gtag.event({
			category: 'Editor',
			action: 'Editor Actions',
			label: 'UnConfirm',
		});

		gtag.event({
			category: 'Editor Actions',
			action: 'UnConfirm',
			label: '',
		});

		nextTick(() => dispatch(saveAll()));
	};

export const setLocked =
	(singleSegment = null) =>
	async (dispatch, getState) => {
		const state = getState()?.traEditor ?? {};
        const currentUser = state.currentUser;
        const segments = (singleSegment ? [singleSegment] : getSelectedSegments(state)).filter((segment) => {
            if (currentUser == null) return true;
            const settings = getSegmentTask(state, segment)?.project?.projectSettings ?? {};
            const allowed = !!settings.general_allowLockUnlockSegments?.checked;
            const inHouseOnly = !!settings.general_allowLockUnlockSegmentsInHouse?.checked;
            return allowed && (!inHouseOnly || String(currentUser?.isInHouse) === '1');
        });

		if (segments.length == 0) return;

		//get locked state from 1st item in selection
		const newLockState = !(segments[0].isLocked ?? false);
		const segmentIDs = segments
			.filter((segment) => segment?.available)
			.map((segment) => segment.id);

		if (segmentIDs.length == 0) return;

		try {
			const response = await ModelSegments.setLockedBulk({
				segmentIDs,
				isLocked: newLockState,
			});

			if (!response?.resp) {
				Notifications.error();
				return;
			}

			const lockActionAt = new Date().toISOString();
			batch(() => {
				segmentIDs.forEach((id) => {
					dispatch({
						type: 'traEditor/updateSegment',
						payload: {
							id,
							isLocked: newLockState,
							lockAction: newLockState ? 'Lock' : 'Unlock',
							lockActionAt,
						},
					});
				});
			});

			gtag.event({
				category: 'Editor Actions',
				action: 'Change Lock',
				label: newLockState ? 'Locked' : 'Unlocked',
			});
		} catch (err) {
			Notifications.error();
		}
	};

export const changeStatus = (newStatus) => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};
	const segments = getSelectedSegments(state);
	if (segments.length == 0) return;

	segments.forEach((S) => {
		if (!segmentIsEditable(S)) return;

		S.forSave = true;
		S.nextStatus = newStatus;
		dispatch({ type: 'traEditor/updateSegment', payload: S });
	});

	gtag.event({
		category: 'Editor Actions',
		action: 'Change Status',
		label: newStatus,
	});

	nextTick(() => dispatch(saveAll()));
};

export const saveAll = (ownTask = false, flushTyping = false) => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};
	if (state.isSwitchingTask && !flushTyping) return false;
	let { task, taskids, activeSegment } = state;

	if (ownTask) {
		task = ownTask;
	}

	let arSegmentsToSave;
	let pObj;

	try {
		arSegmentsToSave = state.arSegments
			.filter((segment) => segmentIsEditable(segment) && (segment.forSave || (flushTyping && segment.hasChanges)))
			.map((S) => {
				const sToSave = { ...S, ...getSegmentContext(state, S, task) };

				sToSave.sourceText = sToSave.sourceText?.replace(/<\/?highlight.*?>/g, '');
				sToSave.translationText = sToSave.translationText?.replace(
					/<\/?highlight.*?>/g,
					''
				);

				return sToSave;
			})
			.toList()
			.toJS();

		if (arSegmentsToSave.length == 0) return true;

		if (arSegmentsToSave.length > 2) {
			dispatch({
				type: 'traEditor/setBatchSaving',
				payload: {
					show: true,
					msg: 'Saving Segments...',
				},
			});
		}

		dispatch({ type: 'traEditor/recalculateConfirmation' });

		pObj = {
			arSegmentsToSave,
			targetLang: task.targetLang,
			project: task.project.id,
			file: task.file.id,
			step: task.step,
			// task: task.id,
			stepOrdr: task.stepOrdr,
			userName:
				state?.currentUser &&
				`${state?.currentUser?.name} ${state?.currentUser?.last_name}`,
			userID: state?.currentUser && state?.currentUser?.id,
		};
	} catch (err) {
		console.log(err);

		Notifications.error('Segments failed to save!');

		gtag.event({
			category: 'Editor Actions',
			action: 'Segments Failed to Save',
			label: '',
		});

		dispatch({
			type: 'traEditor/setBatchSaving',
			payload: {
				show: false,
			},
		});

		return false;
	}

	return trackSegmentSave(ModelSegments.saveAll(pObj))
		.then((r) => {
			if (r) {
				gtag.event({
					category: 'Editor Actions',
					action: 'Segments Saved',
					label: '',
				});

				if (r?.showResults && r?.data?.length > 1) {
					// MyComments
					// Notifications.success(
					// 	`${arSegmentsToSave.length} segments added/updated in ${r?.TMsUpdated} Working TMs.`
					// );
				}

				if (r?.errCode == 'sourceIncosistency') {
					dispatch({
						type: 'traEditor/updateSegments',
						payload: {
							status: 'draft',
							translationText: '',
						},
					});

					Notifications.warning(
						`The following segment(s) ${r.ordr} were not saved due to source text inconsistencies!`
					);

					return false;
				}

				if (!Array.isArray(r.data)) {
					Notifications.error('Segments failed to save!');
					return false;
				}
				dispatch({ type: 'traEditor/acknowledgeSavedSegments', payload: { sent: arSegmentsToSave, saved: r.data } });
				if (r.data.filter((segment) => segment?.id).length !== arSegmentsToSave.length) {
					Notifications.error('Some segments failed to save. Please try again.');
					return false;
				}

				// A repetition-master edit saves through this path (via
				// saveAllSegmentsRef -> Actions.saveAll()) instead of
				// _onlySaveSegment(), which is the only other place that
				// refreshes activeHistory after a save - so the Undo/Redo
				// tab counts went stale for repetition segments specifically
				// until the next full history re-fetch (e.g. a page reload,
				// or switching segments and back). Match _onlySaveSegment's
				// behavior here too.
				if (activeSegment?.id) {
					ModelSegments.getHistoryAndChangesByType('history', {
						taskID: activeSegment.task ?? task.id,
						segmentID: activeSegment.id,
						targetLang: task?.targetLang
					}).then(result => {
						if (result?.history && String(getState().traEditor.activeSegment?.id) === String(activeSegment.id)
							&& String(getState().traEditor.activeSegment?.task) === String(activeSegment.task)) {
							dispatch({
								type: 'traEditor/setActiveHistory',
								payload: result.history,
							});
						}
					});
				}

				return true;
			}

			Notifications.error();

			gtag.event({
				category: 'Editor Actions',
				action: 'Segments Failed to Save',
				label: '',
			});

			return false;
		})
		.catch((err) => {
			Notifications.error();

			gtag.event({
				category: 'Editor Actions',
				action: 'Segments Failed to Save',
				label: '',
			});

			return false;
		})
		.finally(() =>
			dispatch({
				type: 'traEditor/setBatchSaving',
				payload: {
					show: false,
				},
			})
		);
};

export const _saveSegment = async ({ segmentID, dispatch, getState, expectedTask }) => {
	const state = getState()?.traEditor ?? {};
	if (state.isSwitchingTask) return false;
	if (expectedTask != null && String(state.arSegments.getSegment(segmentID)?.task) !== String(expectedTask)) return;
	const user = getState()?.user;
	const isAutoPopulateRepetitions = !!user?.autoPopulateRepetitions;
	const { task } = state;

	//merge extra data to segment
	//get it from state

	const _S = {
		...state.arSegments.getSegment(segmentID),
		targetLang: task.targetLang,
		project: task.project.id,
		file: task.file.id,
		step: task.step,
		// task: task.id,
		stepOrdr: task.stepOrdr,
		...getSegmentContext(state, state.arSegments.getSegment(segmentID)),
		userName:
			state?.currentUser &&
			`${state?.currentUser?.name} ${state?.currentUser?.last_name}`,
		userID: state?.currentUser && state?.currentUser?.id,
	};

	try {
		await trackSegmentSave(ModelSegments.saveSegment({ segment: _S })).then((r) => {
			if (r) {
				if (String(getState().traEditor.arSegments.getSegment(segmentID)?.task) !== String(_S.task)) return;
				if (r?.errCode == 'sourceIncosistency') {
					Notifications.warning(
						`The following segment(s) ${r.ordr} were not saved due to source text inconsistencies!`
					);

					return;
				}

				//TODO: HACK - REVISE THIS why this happens
				if (typeof r['translationText'] != 'undefined')
					delete r.translationText;

				dispatch({ type: 'traEditor/acknowledgeSavedSegments', payload: { sent: [_S], saved: [r] } });

				gtag.event({
					category: 'Editor Actions',
					action: 'Segment Saved',
					label: ``,
				});

				if (_S?.status === SEGMENT_STATUS.CONFIRMED) {
					dispatch(recheckConfirmedSegmentsQA([_S]));
				}

				return;
			}
		});

		const arSegments = getState().traEditor.arSegments.map((S) => {
			if (isAutoPopulateRepetitions && S?.status !== 'confirmed') {
				return S;
			}
			if (S.sameAs != segmentID || S.id == segmentID) return S;

			const newS = { ...S };

			newS.forSave = true;
			Object.assign(newS, getSegmentContext(state, S));
			newS.userName =
				state?.currentUser &&
				`${state?.currentUser?.name} ${state?.currentUser?.last_name}`;
			newS.userID = state?.currentUser && state?.currentUser?.id;

			return newS;
		});

		dispatch({
			type: 'traEditor/setProp',
			payload: { arSegments },
		});

		nextTick(() => dispatch(saveAll()));
	} catch (err) {
		console.log(err);

		Notifications.error('Segment failed to save!');

		gtag.event({
			category: 'Editor Actions',
			action: 'Segment Failed to Save',
			label: '',
		});
	}
};

export const _onlySaveSegment = async ({ segmentID, dispatch, getState, expectedTask }) => {
	const state = getState()?.traEditor ?? {};
	if (state.isSwitchingTask) return false;
	if (expectedTask != null && String(state.arSegments.getSegment(segmentID)?.task) !== String(expectedTask)) return;
	const user = getState()?.user;
	const isAutoPopulateRepetitions = !!user?.autoPopulateRepetitions;
	const { task, activeSegment, taskFiles } = state;

	let fileId = null;
	let segment = state.arSegments.getSegment(segmentID);

	for (const item of taskFiles ?? [])
	{
		if (segment.task == item.taskId) {
			fileId = parseInt(item.id);
		}
	}

	//merge extra data to segment
	//get it from state

	const _S = {
		...state.arSegments.getSegment(segmentID),
		targetLang: task.targetLang,
		project: task.project.id,
		file: fileId ? fileId : task.file.id,
		step: task.step,
		// task: task.id,
		stepOrdr: task.stepOrdr,
		...getSegmentContext(state, state.arSegments.getSegment(segmentID)),
		userName:
			state?.currentUser &&
			`${state?.currentUser?.name} ${state?.currentUser?.last_name}`,
		userID: state?.currentUser && state?.currentUser?.id,
		onlySaveSegment: true,
	};

	try {
		await trackSegmentSave(ModelSegments.saveAll({ segment: _S })).then((r) => {
			if (r) {
				if (String(getState().traEditor.arSegments.getSegment(segmentID)?.task) !== String(_S.task)) return;
				if (r?.errCode == 'sourceIncosistency') {
					Notifications.warning(
						`The following segment(s) ${r.ordr} were not saved due to source text inconsistencies!`
					);

					return;
				}

				//TODO: HACK - REVISE THIS why this happens
				if (typeof r['translationText'] != 'undefined')
					delete r.translationText;

				dispatch({ type: 'traEditor/acknowledgeSavedSegments', payload: { sent: [_S], saved: [r] } });

				activeSegment?.id && ModelSegments.getHistoryAndChangesByType('history', {
					taskID: activeSegment.task ?? task.id,
					segmentID: activeSegment.id,
					targetLang: task?.targetLang
				}).then(result => {
					if (result?.history && String(getState().traEditor.activeSegment?.id) === String(activeSegment.id)
						&& String(getState().traEditor.activeSegment?.task) === String(activeSegment.task)) {
						dispatch({
							type: 'traEditor/setActiveHistory',
							payload: result.history,
						});
					}
				});


				// dispatch({ type: 'traEditor/updateSegment', payload: r });

				gtag.event({
					category: 'Editor Actions',
					action: 'Segment Saved',
					label: ``,
				});

				return;
			}
		});
	} catch (err) {
		console.log(err);

		Notifications.error('Segment failed to save!');

		gtag.event({
			category: 'Editor Actions',
			action: 'Segment Failed to Save',
			label: '',
		});
	}
};

const _debouncedSaveSegment = debounceByParam(
	_saveSegment,
	_.property('segmentID'),
	500
);

const _debouncedOnlySaveSegment = debounceByParam(
	_onlySaveSegment,
	_.property('segmentID'),
	50
);

export const onlySaveSegment = (segmentID, onlySave = false, expectedTask = null) => async (dispatch, getState) => {
	// debounce saving action
	_debouncedOnlySaveSegment({ segmentID, onlySave, dispatch, getState, expectedTask: expectedTask ?? getState().traEditor.arSegments.getSegment(segmentID)?.task });
};

export const saveSegment = (segmentID) => async (dispatch, getState) => {
	// debounce saving action
	_debouncedSaveSegment({ segmentID, dispatch, getState, expectedTask: getState().traEditor.arSegments.getSegment(segmentID)?.task });
};

export const copyAllTagsToTarget = () => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};
	const activeSegment = state?.activeSegment ?? null;

	if (!activeSegment) return;

	const row = document
		.getElementById('editorRow_' + activeSegment.id)
		.querySelectorAll('.src .xlftag');
	if (!row.length) return;

	let carretPosition = activeEditor?.getSelection()?.index ?? 0;
	row.forEach((img) => {
		window.activeEditor.insertEmbed(
			carretPosition,
			'xlfTag',
			{
				src: img.getAttribute('src'),
				className: img.getAttribute('class'),
				dataType: img.getAttribute('data-type'),
				dataTag: img.getAttribute('data-tag'),
				dataOriginal: img.getAttribute('data-original'),
				dataIdx: img.getAttribute('data-idx'),
			},
			'user'
		);
		//move selection
		carretPosition++;
		nextTick(() => window.activeEditor.setSelection(carretPosition, 0));
	});

	gtag.event({
		category: 'Editor Actions',
		action: 'Copy all tags',
		label: ``,
	});
};

export const copyNextTagToTarget = () => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};
	const activeSegment = state?.activeSegment ?? null;

	if (!activeSegment) return;

	const row = document.getElementById('editorRow_' + activeSegment.id);
	if (!row) return;

	const tagsInSource = row.querySelectorAll('.src .xlftag');
	if (!tagsInSource.length) return;

	let carretPosition = activeEditor?.getSelection()?.index ?? 0;
	let needsUpdate = false;

	for (let i = 0; i < tagsInSource.length; i++) {
		const img = tagsInSource[i];

		const tagAttr = {
			src: img.getAttribute('src'),
			className: img.getAttribute('class'),
			dataType: img.getAttribute('data-type'),
			dataTag: img.getAttribute('data-tag'),
			dataOriginal: img.getAttribute('data-original'),
			dataIdx: img.getAttribute('data-idx'),
			dataDirection: 'L',
		};

		const tagInTrg = row.querySelector(
			`.trg .ql-editor img[data-type="${tagAttr.dataType}"][data-tag="${tagAttr.dataTag}"][data-idx="${tagAttr.dataIdx}"]`
		);

		if (tagInTrg == null) {
			//insert and break;
			window.activeEditor.insertEmbed(
				carretPosition,
				'xlfTag',
				tagAttr,
				'user'
			);
			//move selection
			carretPosition++;
			nextTick(() => window.activeEditor.setSelection(carretPosition, 0));
			needsUpdate = true;
			break;
		}
	}

	gtag.event({
		category: 'Editor Actions',
		action: 'Copy next inline tag',
		label: ``,
	});
};

export const clearTargetTags = () => async (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};
	const activeSegment = state?.activeSegment ?? null;

	const arSelectedSegments = state.arSegments
		.filter(({ checked }) => checked)
		.toList()
		.toJS();

	if (arSelectedSegments.length > 1) {
		for (const s of arSelectedSegments) {
			const segmentId = s?.id;
			const taskId = s?.task;

			const newSegment = await ModelSegments.clearTargetTags(segmentId, taskId);

			if (!s) {
				break;
			}

			dispatch({
				type: 'traEditor/updateSegment',
				payload: {
					id: newSegment.id,
					translationText: newSegment.translationText,
					status: SEGMENT_STATUS.DRAFT
				},
			});
		}
		return;
	}

	const segmentId = activeSegment?.id;
	const taskId = activeSegment?.task;

	const segment = await ModelSegments.clearTargetTags(segmentId, taskId);

	if (!segment) {
		return;
	}

	dispatch({
		type: 'traEditor/updateSegment',
		payload: {
			id: segment.id,
			translationText: segment.translationText,
			status: SEGMENT_STATUS.DRAFT
		},
	});
}

export const getMissingTags = () => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};
	const activeSegment = state?.activeSegment ?? null;

	if (!activeSegment) return;

	const row = document.getElementById('editorRow_' + activeSegment.id);
	if (!row) return;

	const tagsInSource = row.querySelectorAll('.src .xlftag');
	if (!tagsInSource.length) return;

	const missingTags = [];

	for (let i = 0; i < tagsInSource.length; i++) {
		const img = tagsInSource[i];

		const tagAttr = {
			src: img.getAttribute('src'),
			className: img.getAttribute('class'),
			dataType: img.getAttribute('data-type'),
			dataTag: img.getAttribute('data-tag'),
			dataOriginal: img.getAttribute('data-original'),
			dataIdx: img.getAttribute('data-idx'),
			dataDirection: 'L',
		};

		const tagInTrg = row.querySelector(
			`.trg .ql-editor img[data-type="${tagAttr.dataType}"][data-tag="${tagAttr.dataTag}"][data-idx="${tagAttr.dataIdx}"]`
		);

		if (tagInTrg == null) {
			missingTags.push({
				forDisplay: img, // To show in popup
				forUse: tagAttr, // To append in target if selected
			});
		}
	}

	if (missingTags.length === 0) {
		return;
	}

	const ev = new CustomEvent('missingTagsSuggestionClicked', {
		detail: {
			el: row.querySelector('.trg'),
			editor: window.activeEditor,
			segmentid: activeSegment.id,
			missingTags,
		},
	});

	document.dispatchEvent(ev);
};

export const appendTagsToTarget = (tags) => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};
	const activeSegment = state?.activeSegment ?? null;

	if (!activeSegment) return;

	const row = document.getElementById('editorRow_' + activeSegment.id);
	if (!row) return;

	let _tags = tags;
	// Wrap in array if single tag passed
	if (!Array.isArray(tags)) {
		_tags = [_tags];
	}

	let carretPosition = activeEditor?.getSelection()?.index ?? 0;

	_tags.forEach((tag) => {
		window.activeEditor.insertEmbed(carretPosition, 'xlfTag', tag, 'user');
		// Move selection
		carretPosition++;
		nextTick(() => window.activeEditor.setSelection(carretPosition, 0));
	});
};

export const _sortSegments = (segments, sort) => {
	const isSorted = sort && Object.values(sort).some(Boolean);

	if (!isSorted) {
		return;
	}

	return segments.sort((a, b) => {
		if (sort.sourceAZ) {
			if (a.sourceText < b.sourceText) return -1;
			if (a.sourceText > b.sourceText) return 1;
		} else if (sort.sourceZA) {
			if (a.sourceText > b.sourceText) return -1;
			if (a.sourceText < b.sourceText) return 1;
		} else if (sort.targetAZ) {
			if (a.translationText < b.translationText) return -1;
			if (a.translationText > b.translationText) return 1;
		} else if (sort.targetZA) {
			if (a.translationText > b.translationText) return -1;
			if (a.translationText < b.translationText) return 1;
		} else if (sort.shortestFirstSource) {
			if (String(a.sourceText).length < String(b.sourceText).length) return -1;
			if (String(a.sourceText).length > String(b.sourceText).length) return 1;
		} else if (sort.shortestFirstTarget) {
			if (String(a.translationText).length < String(b.translationText).length)
				return -1;
			if (String(a.translationText).length > String(b.translationText).length)
				return 1;
		} else if (sort.longestFirstSource) {
			if (String(a.sourceText).length > String(b.sourceText).length) return -1;
			if (String(a.sourceText).length < String(b.sourceText).length) return 1;
		} else if (sort.longestFirstTarget) {
			if (String(a.translationText).length > String(b.translationText).length)
				return -1;
			if (String(a.translationText).length < String(b.translationText).length)
				return 1;
		} else if (sort.lowestMatchFirst) {
			if (Number(a.matchScore) < Number(b.matchScore)) return -1;
			if (Number(a.matchScore) > Number(b.matchScore)) return 1;
		} else if (sort.highestMatchFirst) {
			if (Number(a.matchScore) > Number(b.matchScore)) return -1;
			if (Number(a.matchScore) < Number(b.matchScore)) return 1;
		}

		return a.ordr > b.ordr ? 1 : -1;
	});
};

let previousArgs = null;
let previousValue;

export const sortSegments = (...args) => {
	const [segmentsA, sortA, taskOptionsA] = args;
	const [segmentsB, sortB, taskOptionsB] = previousArgs ?? [];
	previousArgs = args;

	const isSorted = sortA && Object.values(sortA).some(Boolean);

	const sortedSegments = [];

	if (!isSorted) {
		for (let sid of getUniqueSegmentIds(taskOptionsA)) {
			if (segmentsA.hasSegment(sid)) {
				sortedSegments.push(segmentsA.getSegment(sid));
			}
		}
		return sortedSegments;
	}

	if (isSorted && previousValue && sortA === sortB) {
		for (let sid of previousValue.map((_s) => _s.id)) {
			sortedSegments.push(segmentsA.getSegment(sid));
		}
		return sortedSegments;
	}

	previousValue = _sortSegments(segmentsA.toList().toJS(), sortA);
	return previousValue;
};

export const moveUp =
	(pos = 'end') =>
	(dispatch, getState) => {
		const {
			activeSegment,
			arDisplaySegments,
			segmentsFilters: { sort },
		} = getState()?.traEditor ?? {};
		if (!activeSegment) return;

		const taskOptions = getTaskOptions(getState());
		const segments = sortSegments(arDisplaySegments, sort, taskOptions);

		let newFocusedSegment = null;
		for (const segment of segments) {
			if (segment.id == activeSegment.id) break;

			if (segment.isLocked || segment.isRepetition || segment.isRevisionLocked)
				continue;

			newFocusedSegment = segment;
		}
		if (!newFocusedSegment) return;

		dispatch({
			type: 'traEditor/setActiveSegment',
			payload: newFocusedSegment,
		});
		nextTick(() =>
			dispatch(gotoSegmentById(newFocusedSegment?.id, true, 'auto', pos))
		);
	};

export const moveDown = () => (dispatch, getState) => {
	const {
		activeSegment,
		arDisplaySegments,
		segmentsFilters: { sort },
	} = getState()?.traEditor ?? {};
	if (!activeSegment) return;

	const taskOptions = getTaskOptions(getState());

	const segments = sortSegments(arDisplaySegments, sort, taskOptions).reverse();

	let newFocusedSegment = null;
	for (const segment of segments) {
		if (segment.id == activeSegment.id) break;

		if (segment.isLocked || segment.isRepetition || segment.isRevisionLocked)
			continue;

		newFocusedSegment = segment;
	}
	if (!newFocusedSegment) return;

	dispatch({ type: 'traEditor/setActiveSegment', payload: newFocusedSegment });
	nextTick(() =>
		dispatch(gotoSegmentById(newFocusedSegment?.id, true, 'auto', 'start'))
	);
};

export const moveToNextSegment = () => (dispatch, getState) => {
	const editorState = getState().traEditor;
	const isTaskBlocked = (segment) => {
		if (!editorState.openedWithHash || editorState.openedAsView) return false;
		const task = getSegmentTask(editorState, segment);
		return shouldDisableSegmentsByTaskStatus(task?.status) || task?.isContinuousWorkflowLocked;
	};
	const {
		arDisplaySegments,
		activeSegment,
		segmentsFilters: { sort },
	} = getState()?.traEditor ?? {};
	if (!activeSegment) return;

	const userPreferences = getState()?.user;

	if (!userPreferences?.goToUnconfirmed) {
		return;
	}

	const taskOptions = getTaskOptions(getState());
	const segments = sortSegments(arDisplaySegments, sort, taskOptions);

	const activeSegmentIndex = segments.findIndex(
		(S) => S.id == activeSegment.id
	);

	// Walk segments with a plain positional index instead of calling
	// segments.findIndex() again for every element - on a large task (tens of
	// thousands of segments) that turned this into an O(n^2) scan that ran
	// synchronously on the main thread right when Ctrl+Enter was pressed,
	// before the save request even started.
	let newFocusedSegment = null;
	let newFocusedSegmentIndex = -1;
	for (let i = 0; i < segments.length; i++) {
		const segment = segments[i];
		const skip = [
			i <= activeSegmentIndex,
			!segmentIsEditable(segment),
			isTaskBlocked(segment),
			segment.status == 'confirmed',
			segment.isRepetition && segment.sameAs > 0,
		].some(Boolean);
		if (skip) continue;

		newFocusedSegment = segment;
		newFocusedSegmentIndex = i;
		break;
	}
	// If nothing unconfirmed was found ahead of the current position, wrap
	// around and take the earliest unconfirmed segment from the start of the
	// task - not just when sitting on the very last segment. Otherwise, once
	// everything *after* the current position is confirmed/locked, this would
	// silently stop moving even though an earlier unconfirmed segment exists.
	if (!newFocusedSegment) {

		for (let i = 0; i < segments.length; i++) {
			const segment = segments[i];
			const skip = [
				!segmentIsEditable(segment),
				isTaskBlocked(segment),
				segment.status == 'confirmed',
				segment.isRepetition && segment.sameAs > 0,
			].some(Boolean);
			if (skip) continue;

			newFocusedSegment = segment;
			newFocusedSegmentIndex = i;
			break;
		}
	}

	if (!newFocusedSegment) return;

	dispatch({ type: 'traEditor/setActiveSegment', payload: newFocusedSegment });
	// Go straight to moveEditorToLine with the index we already found here,
	// instead of gotoSegmentById(id) - that helper (and moveEditorToLine
	// itself) each independently call sortSegments() again and re-search for
	// the segment's index, so going through it meant rebuilding the full
	// (possibly tens-of-thousands-long) sorted segment list three times over
	// for a single navigation. We already have both the sorted list and the
	// index, so skip straight to the one place that actually needs them.
	nextTick(() =>
		dispatch(moveEditorToLine(newFocusedSegmentIndex, true, 'auto'))
	);
};

export const allAllSegmentsTranslated = () => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};

	return state.arSegments.every((S) => {
		return S.translationText != '' && S.translationText != null;
	});
};

export const actionPrepareDownload =
	(options = {}) =>
	(dispatch, getState) => {
		const state = getState()?.traEditor ?? {};

		let optionTask = null;

		if (options?.task) {
			optionTask = options.task;
		}

		const projectValue = optionTask ? optionTask?.file?.project : state?.task?.file?.project ?? 0;
		const project = projectValue?.id ?? projectValue ?? 0;
		const file = optionTask ? optionTask?.file?.id : state?.task?.file?.id ?? 0;
		const task = optionTask ? optionTask?.id : state?.task?.id ?? 0;

		const _n = Notifications.info('Preparing download...');

		const exportType = options?.type === 'current' ? 'current' : 'final';
		const downloadUrl = `/direct-backend/downloads/${project}/files/${file}/task/${task}/translation?type=${exportType}`;
		dispatch({
			type: 'traEditor/setBatchSaving',
			payload: {
				show: true,
				msg: 'Downloading...',
			},
		});

		return ModelFiles.prepareDownload(project, file, task, options)
			.then((r) => {
				if (r?.resp) {
					if (r?.zip) {
						const zipProject = r?.file?.projectID ?? project;
						const zipDownloadUrl = `/direct-backend/downloads/${zipProject}/zip/${r?.download}`;
						gtag.event({
							category: 'Editor Actions',
							action: 'Download File',
							label: `${zipDownloadUrl}`,
						});

						downloadTranslationDirect(
							zipDownloadUrl,
							`${r?.projectName}.zip`
						);
						return;
					}

					gtag.event({
						category: 'Editor Actions',
						action: 'Download File',
						label: `${downloadUrl}`,
					});

					downloadTranslationDirect(
						r.downloadUrl ?? downloadUrl,
						r.fileNameToSave
					);
					return;
				}

				if (r?.status === 'error') {
					dispatch({
						type: 'traEditor/showDialog',
						payload: {
							key: 'exportErrors',
							data: [{...r}]
						},
					});

					Notifications.error(`An error occurred during export. Please try again.`);
				} else {
					Notifications.error(r?.message || 'Could not prepare the download.');
				}

				gtag.event({
					category: 'Editor Actions',
					action: 'Download File FAILED',
					label: `${downloadUrl}`,
				});
			})
			.catch((err) => {
				console.log(err);
				Notifications.error('Download failed. Please try again.');

				gtag.event({
					category: 'Editor Actions',
					action: 'Download File FAILED',
					label: `${downloadUrl}`,
				});
			})
			.finally(() => {
				_n.hide();
				dispatch({
					type: 'traEditor/setBatchSaving',
					payload: {
						show: false,
					},
				});
		});
	};

export const actionPrepareTrackChangesDownload =
	(options = {}) =>
	(dispatch, getState) => {
		const state = getState()?.traEditor ?? {};
		const project = state?.task?.file?.project?.id ?? state?.task?.file?.project ?? 0;
		const file = state?.task?.file?.id ?? 0;
		const task = state?.task?.id ?? 0;

		const _n = Notifications.info('Preparing tracked changes...');
		dispatch({
			type: 'traEditor/setBatchSaving',
			payload: {
				show: true,
				msg: 'Downloading...',
			},
		});

		return ModelFiles.prepareTrackChangesDownload(project, file, task, options)
			.then((r) => {
				if (r?.resp) {
					const downloadUrl = r?.downloadUrl ?? `/direct-backend/downloads/${project}/track-changes/${r?.download}`;

					gtag.event({
						category: 'Editor Actions',
						action: 'Download Tracked Changes',
						label: `${downloadUrl}`,
					});

					downloadTranslationDirect(downloadUrl, r.fileNameToSave);
					return;
				}

				Notifications.error(r?.message || 'Unable to prepare tracked changes.');

				gtag.event({
					category: 'Editor Actions',
					action: 'Download Tracked Changes FAILED',
					label: `${project}/${file}/${task}`,
				});
			})
			.catch((err) => {
				console.log(err);
				Notifications.error('Download failed. Please try again.');

				gtag.event({
					category: 'Editor Actions',
					action: 'Download Tracked Changes FAILED',
					label: `${project}/${file}/${task}`,
				});
			})
			.finally(() => {
				_n.hide();
				dispatch({
					type: 'traEditor/setBatchSaving',
					payload: {
						show: false,
					},
				});
			});
	};

export const spellCheck = (term, segmentID) => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};
	const userState = getState()?.user ?? {};

	if (userState?.spellChecking === 'browser') {
		return;
	}

	const targetLang = state?.task?.targetLang ?? '';

	ModelSpellCheck.query({
		term: term
			.replace(/&lt;/gm, '<')
			.replace(/&gt;/gm, '>')
			.replace(/<[^>]+>/gm, ''),
		lang: targetLang,
	})
		.then((r) => {
			dispatch({
				type: 'traEditor/updateSpellCheckSuggestions',
				payload: { id: segmentID, data: r },
			});
		})
		.catch((err) => {
			console.log(err);
		});
};

export const toggleQAIgnore =
	(segmentID, type, newState) => (dispatch, getState) => {
		const state = getState()?.traEditor ?? {};

		const owner = getSegmentTask(state, state.arSegments.getSegment(segmentID));
		const taskID = owner?.id;
		const stepID = owner?.step;

		const activeUser = state?.assignedUserFullName ?? null;

		gtag.event({
			category: 'Editor Actions',
			action: 'QA Ignored',
			label: `${type}`,
		});

		ModelQA.toggleIgnore({ segmentID, type, newState, activeUser, taskID, stepID })
			.then((r) => {
				if (r?.resp) {
					const latest = getState().traEditor;
					const taskResults = latest.qaResultsByTask?.[taskID] ?? {};
					const issues = taskResults[segmentID] ?? state.arSegments.getSegment(segmentID)?.qaResults ?? [];
					dispatch({ type: 'traEditor/setProp', payload: { qaResultsByTask: {
						...latest.qaResultsByTask, [taskID]: { ...taskResults, [segmentID]: issues.map((issue) => issue.qaTest === type ? { ...issue, isIgnored: !!newState } : issue) },
					} } });
					if (String(getSegmentTask(latest, latest.arSegments.getSegment(segmentID))?.id) !== String(taskID)) return;
					dispatch({
						type: 'traEditor/updateSegmentQAIgnore',
						payload: { segmentID, type, newState },
					});
				}
			})
			.catch((err) => console.log(err));
	};

export const getCheckedQAOptions = (projectSettings = {}) => {
	const checkedQAOptions = {};

	for (const property in projectSettings) {
		if (property === 'qa_run_on_sample') {
			continue;
		}

		if (projectSettings[property]?.checked) {
			checkedQAOptions[`${property}`] = {
				checked: true,
				value: projectSettings[property]?.value || null,
			};
		}
	}

	checkedQAOptions.qa_skip_on_the_fly_check = {
		checked: true,
		value: null,
	};

	return checkedQAOptions;
};

const mapQAResultsToSegments = (results = {}) => {
	const updatedSegments = [];

	for (const [key, value] of Object.entries(results)) {
		updatedSegments.push({
			id: parseInt(key),
			qaResults: value,
		});
	}

	return updatedSegments;
};

const mapQAResultsToSegmentsByIds = (results = {}, segmentIds = []) =>
	segmentIds.map((segmentId) => ({
		id: parseInt(segmentId),
		qaResults: results?.[segmentId] ?? results?.[parseInt(segmentId)] ?? [],
	}));

const hasActiveQAIssues = (results = {}) =>
	Object.values(results).some((segmentResults = []) =>
		segmentResults.some((issue) => !issue?.isIgnored)
	);

const getQAOptionsFromExistingResults = (state) => {
	const options = {};
	const resolveQAOption = (qaTest) => {
		if (qaTest?.startsWith('qa_linguistic_check_')) {
			return 'qa_linguistic_check';
		}

		if (qaTest?.startsWith('qa_ai_spell_check_')) {
			return 'qa_ai_spell_check';
		}

		return qaTest;
	};

	state?.arSegments?.forEach((segment) => {
		(segment?.qaResults ?? []).forEach((issue) => {
			if (!issue?.qaTest) {
				return;
			}

			options[resolveQAOption(issue.qaTest)] = {
				checked: true,
				value: null,
			};
		});
	});

	if (Object.keys(options).length === 0) {
		return null;
	}

	return {
		...options,
		qa_skip_on_the_fly_check: {
			checked: true,
			value: null,
		},
	};
};

export const runQATask = (options, config = {}) => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};
	const user = getState()?.user ?? {};
	const doubleQuote = user?.doubleQuotes ?? '';
	const singleQuote = user?.singleQuotes ?? '';
	const { showResults = true, trigger = 'manual', segments: configSegments = null, segmentIds = null } = config;

	const qaTask = getSegmentTask(state, configSegments?.[0] ?? state.activeSegment);
	const taskID = qaTask?.id;
	const taskIDs = state.joinedTaskFocus && trigger !== 'task_completion' ? [state.joinedTaskFocus] : state?.taskids;
	const stepID = qaTask?.step;
	const segments = configSegments ?? state.arSegments;
	const isSegmentScoped = Array.isArray(segmentIds) && segmentIds.length > 0;
	const viewID = state?.openedAsView ? state?.viewID : null;
	const requestData = {
		taskID,
		stepID,
		options,
		doubleQuote,
		singleQuote,
		trigger,
		structured_output: true,
	};
	if (viewID) {
        requestData.viewID = viewID;
    }
    if (trigger === 'task_completion' && state.joinedTasks?.length > 1) {
        requestData.optionsByTask = Object.fromEntries(state.joinedTasks.map((task) => [task.id, getCheckedQAOptions(task.project?.projectSettings ?? {})]));
    }

	// Full-task QA must not serialize the complete Redux segment map. Apart
	// from slowing the request down, large tasks exceed the proxy request-body
	// limit before the backend can run any QA. Keep the current client segment
	// only for a single on-the-fly confirmation check. For a small multi-segment
	// recheck, send ids and let the backend load the authoritative data.
	if (isSegmentScoped) {
		if (segments.length === 1) {
			requestData.segments = segments;
		} else {
			requestData.segmentIds = segmentIds;
		}
	}

	gtag.event({
		category: 'Editor Actions',
		action: 'Run QA',
		label: 'Task ' + taskID,
	});

	dispatch({ type: 'traEditor/runQATask' });

	if (!isSegmentScoped) {
		dispatch({
			type: 'traEditor/setProp',
			payload: {
				lastQAOptions: options,
			},
		});
	}

	const request = !isSegmentScoped && taskIDs?.length > 1
		? ModelQA.checkTasks({ ...requestData, taskIDs })
		: ModelQA.checkTask(requestData);

	return request
		.then((r) => {
			const results = r?.structured_output?.results ?? r ?? {};
			const incomingByTask = r?.structured_output?.resultsByTask ?? { [taskID]: results };
			const latest = getState().traEditor;
			const qaResultsByTask = { ...latest.qaResultsByTask };
			for (const [id, taskResults] of Object.entries(incomingByTask)) {
				qaResultsByTask[id] = isSegmentScoped
					? { ...qaResultsByTask[id], ...Object.fromEntries(segmentIds.map((segmentID) => [segmentID, taskResults[segmentID] ?? []])) }
					: taskResults;
			}
			dispatch({ type: 'traEditor/setProp', payload: { qaResultsByTask } });
			const patches = [];
			latest.arSegments.forEach((segment) => {
				const owner = getSegmentTask(latest, segment);
				if (!Object.prototype.hasOwnProperty.call(incomingByTask, owner.id)) return;
				if (isSegmentScoped && !segmentIds.some((id) => String(id) === String(segment.id))) return;
				const snapshot = isSegmentScoped ? configSegments?.find((item) => String(item.id) === String(segment.id)) : null;
				if (snapshot && (String(snapshot.task) !== String(segment.task) || snapshot.translationText !== segment.translationText)) return;
				patches.push({ id: segment.id, qaResults: qaResultsByTask[owner.id]?.[segment.id] ?? [] });
			});
			dispatch({ type: 'traEditor/updateSegments', payload: patches });

			dispatch({
				type: 'traEditor/hideDialog',
				payload: {
					key: 'runQA',
				},
			});

			if (showResults) {
				if (user?.qaPaneEnabled !== false) {
					dispatch({
						type: 'traEditor/hideDialog',
						payload: {
							key: 'QAResults',
						},
					});
					dispatch({
						type: 'traEditor/setProp',
						payload: {
							qaPaneActive: true,
						},
					});
				} else {
					dispatch({
						type: 'traEditor/showDialog',
						payload: {
							key: 'QAResults',
						},
					});
				}
			}

			return {
				results,
				summary: r?.structured_output?.summary ?? null,
				hasIssues: hasActiveQAIssues(results),
			};
		})
		.catch((err) => {
			console.log(err);
			throw err;
		})
		.finally(() => dispatch({ type: 'traEditor/stopQATask' }));
};

export const recheckConfirmedSegmentsQA = (confirmedSegments = []) => (dispatch, getState) => {
    const state = getState()?.traEditor ?? {};
    if (!Array.isArray(confirmedSegments)) return;
    const groups = new Map();
    for (const segment of confirmedSegments) {
        const owner = getSegmentTask(state, segment);
        const group = groups.get(owner.id) ?? { owner, segments: [] };
        group.segments.push({ ...state.arSegments.getSegment(segment.id), ...segment });
        groups.set(owner.id, group);
    }
    for (const { owner, segments } of groups.values()) {
        const options = state.lastQAOptions ??
            (state.joinedTasks?.length > 1 ? null : getQAOptionsFromExistingResults(state)) ??
            getCheckedQAOptions(owner.project?.projectSettings ?? {});
        if (!segments.length || Object.keys(options).length <= 1) continue;
        dispatch(runQATask(options, {
            showResults: false, trigger: 'segment_confirm', segments,
            segmentIds: segments.map((segment) => segment.id),
        }));
    }
};

export const moveEditorToLine =
	(idx, focus = false, scrollType = 'auto', pos = 'start') =>
	(dispatch, getState) => {
		//get segment id to focus after
		const {
			arDisplaySegments,
			segmentsFilters: { sort },
		} = getState().traEditor;
		const taskOptions = getTaskOptions(getState());
		const segments = sortSegments(arDisplaySegments, sort, taskOptions);
		const sid = segments[idx].id;

		let triesToMove = 0;

		let $nextRow = document.querySelector(`#editorRow_${sid}`);
		if (!isGridRowVisible($nextRow)) scrollType = 'center';

		function _setFocus() {
			const _ed = document.querySelector(
				'#editorRow_' + sid + ' .ql-editor > *:first-child'
			);

			if (!_ed || !focus || !sid) return;

			const range = document.createRange();
			range.selectNodeContents(_ed);
			range.collapse(pos === 'start' ? true : false);
			const sel = window.getSelection();
			sel.removeAllRanges();
			sel.addRange(range);
			_ed.focus();
		}

		function _moveGrid() {
			dispatch({
				type: 'traEditor/goToSegment',
				payload: {
					indexToGo: idx,
					scrollToAlignment: scrollType,
				},
			});

			nextTick(() => dispatch({ type: 'traEditor/resetGoToSegment' }));
			nextTick(() => dispatch({ type: 'traEditor/reRender' }));

			setTimeout(async () => {
				triesToMove++;
				$nextRow = document.querySelector(`#editorRow_${sid}`);

				if (!isGridRowVisible($nextRow) && triesToMove < 10) {
					_moveGrid();
				}

				if (isGridRowVisible($nextRow)) {
					_setFocus();
				}
			}, 50);
		}

		_moveGrid();
	};

export const gotoSegmentById =
	(id, focus = false, moveType = 'auto', pos) =>
	(dispatch, getState) => {
		const {
			arDisplaySegments,
			segmentsFilters: { sort },
		} = getState()?.traEditor ?? {};
		const taskOptions = getTaskOptions(getState());
		const segments = sortSegments(arDisplaySegments, sort, taskOptions);
		const foundIndex = segments.findIndex((S) => S.id == id);
		if (foundIndex == -1) return;

		dispatch(moveEditorToLine(foundIndex, focus, moveType, pos));
	};

// The "Go to segment" dialog takes the displayed row number, which is
// segment.ordr - a file-wide value that only starts at 1 for an unsplit
// task or a split task's first part. For any later part, ordr continues
// from where the previous part left off, so a later part's own position-0
// segment can already have ordr > 1. Looking the segment up by its actual
// ordr (instead of assuming ordr - 1 is the position within this part's
// own array) is what makes this work for split task parts too.
export const gotoSegmentByOrdr =
	(ordr, focus = false, moveType = 'auto', pos) =>
	(dispatch, getState) => {
		const {
			arDisplaySegments,
			segmentsFilters: { sort },
		} = getState()?.traEditor ?? {};
		const taskOptions = getTaskOptions(getState());
		const segments = sortSegments(arDisplaySegments, sort, taskOptions);
		const foundIndex = segments.findIndex((S) => S.ordr == ordr);
		if (foundIndex == -1) return;

		dispatch(moveEditorToLine(foundIndex, focus, moveType, pos));
	};

export const setSelectedSegmentById = (id) => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};

	const arSegments = state.arDisplaySegments;

	const seg = arSegments?.getSegment(id);

	if (!seg) {
		dispatch({ type: 'traEditor/setActiveSegment', payload: null });
		return;
	}

	dispatch({ type: 'traEditor/setActiveSegment', payload: { ...seg } });
};

export const repetitionSetSync = (type, segment, segmentId = null) => (dispatch, getState) => {
	const taskIds = getState()?.traEditor?.joinedTaskFocus ? [getState().traEditor.joinedTaskFocus] : getState()?.traEditor?.taskids;
	const arSegments = getState()?.traEditor.arSegments;

	if (!segment && segmentId) {
		segment = arSegments.getSegment(segmentId);
	}

	if (segment?.sameAs < 0 && type === 'remove' && segmentId) {
		return;
	}

	ModelSegments.repetitionSetSync({ type, segmentID: segment.id, segment, taskIds })
		.then((r) => {
			if (r?.resp) {
				// Only patch the field the backend actually changed. This used
				// to spread the whole `segment` snapshot captured above - taken
				// synchronously, before whatever the user typed while this
				// request was in flight got dispatched - so by the time this
				// resolves it would stomp the segment's translationText back
				// to what it was before that keystroke, making the first
				// character typed into a repetition look like it vanished.
				dispatch({
					type: 'traEditor/updateSegment',
					payload: { id: segment.id, sameAs: r.sameAs },
				});
			}
		})
		.catch((err) => {
			console.log(err);
		});
};

export const firstRepetitionSetSync = (type, segment) => (dispatch, getState) => {
	const taskIds = getState()?.traEditor?.joinedTaskFocus ? [getState().traEditor.joinedTaskFocus] : getState()?.traEditor?.taskids;
	const segments = getState()?.traEditor?.arSegments;

	const segmentId = parseInt(segment.id);

	const filtredSegments = segments.filter((S) => Math.abs(parseInt(S.sameAs)) === segmentId || S.id == segmentId);

	ModelSegments.firstRepetitionSetSync({ type, segmentID: segment.id, taskIds, segment, filtredSegments })
		.then((r) => {
			if (r?.resp) {
				let segments = r.segments;

				for (const item of segments) {
					const newSeg = {
						...item,
					};

					dispatch({ type: 'traEditor/updateSegment', payload: newSeg });
				}
			}
		})
		.catch((err) => {
			console.log(err);
		});
};

export const clearFilters = () => (dispatch, getState) => {
	dispatch({ type: 'traEditor/clearSegmentsFilters' });

	gtag.event({
		category: 'Editor Actions',
		action: 'Clear All Filters',
		label: '',
	});

	//restore scroll to last active segment
	dispatch(resetFocusToLastActiveSegment());
};

export const resetFocusToLastActiveSegment = () => (dispatch, getState) => {
	nextTick(() => {
		dispatch({ type: 'traEditor/filterAllSegments' });

		const _aS = getState().traEditor?.activeSegment;
		if (_aS) nextTick(() => dispatch(gotoSegmentById(_aS.id, true, 'center')));
	});
};

export const autoPreTransMT = (options) => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};

	const taskID = state?.task?.realID ?? state?.task?.id;

	const pObj = {
		preTranslationSettings: { ...options },
		tasks: [taskID],
	};

	gtag.event({
		category: 'Editor Actions',
		action: 'Pretrans MT',
		label: 'Task ' + taskID,
	});

	ModelFiles.preTranslate(pObj)
		.then(() => {
			dispatch({
				type: 'traEditor/setProp',
				payload: { autoTranslating: true },
			});
		})
		.catch((err) => {
			console.log(err.body);
		});
};

export const setSynonymProps = (props) => (dispatch) => {
	dispatch({
		type: 'traEditor/setSynonymsProp',
		payload: {
			...props,
		},
	});
};

export const moveUpDownSuggestion = (moveUp = true) => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};

	const suggestions = state?.filtredSuggestions ?? [];
	const activeSuggestion = state?.activeSuggestion ?? [];

	let indexActiveSuggestions = suggestions.findIndex(elem => elem?.metas?.id === activeSuggestion?.metas?.id);

	if (indexActiveSuggestions === -1) {
		indexActiveSuggestions = 0;
	}

	let newActiveSuggestion = null;

	if (moveUp) {
		newActiveSuggestion = suggestions[indexActiveSuggestions - 1] ?? null;
	} else {
		newActiveSuggestion = suggestions[indexActiveSuggestions + 1] ?? null;
	}

	if (newActiveSuggestion) {
		dispatch({ type: 'traEditor/setActiveSuggestion', payload: newActiveSuggestion });

		let suggestionRowElem = document.querySelector(`.suggestion-id-${newActiveSuggestion?.metas?.id}`);
		let allSuggestionRows = document.querySelectorAll('.suggestionsSidebar .suggestionRow');

		if (suggestionRowElem && allSuggestionRows) {
			allSuggestionRows.forEach(elem => {
				elem.classList?.remove('suggestionRowSelected');
			})
			suggestionRowElem.classList.add('suggestionRowSelected');
			suggestionRowElem.scrollIntoView({behavior: "smooth"});
		}
	}
};

export const setUntermProps = (props) => (dispatch) => {
	dispatch({
		type: 'traEditor/setUntermsProp',
		payload: {
			...props,
		},
	});
};

export const setLangPairUnterm = () => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};

	const targetLang = state?.task?.targetLang;
	const sourceLang = state?.task?.sourceLang;

	dispatch({
		type: 'traEditor/setUntermsProp',
		payload: {
			langs: {
				src: sourceLang,
				trg: targetLang
			}
		},
	});
}

export const searchUnterm = () => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};

	const targetLang = state?.task?.targetLang;
	const { unterms } = state;
	const { term, langs, matchTypeUnterm } = unterms;

	ModelConcordance.requestUnterm({
		lang: targetLang,
		expression: term,
		targetLang: langs.trg,
		sourceLang: langs.src,
		matchType: matchTypeUnterm
	}).then(r => {
		if (r) {
			dispatch(setUntermProps({
				results: r
			}));
		}
	})
}

export const searchSynonym = () => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};

	const { synonyms } = state;
	const { term, lang } = synonyms;

	ModelConcordance.requestSynonym({
		lang: lang,
		expression: term
	}).then(r => {
		if (r) {
			dispatch(setSynonymProps(r));
		}
	})
}


export const searchConcordance = (searchOverrides = {}) => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};

	const taskID = state?.task?.id;
	const projectID = state?.task?.project?.id;
	const { concordance } = state;
	const searchParams = {
		...concordance,
		...searchOverrides,
	};
	const { searchTo, matchType, searchAllTMs, term } = searchParams;
	const requestKey = JSON.stringify({
		term,
		matchType,
		searchTo,
		searchAllTMs,
		projectID,
		taskID,
	});

	dispatch({
		type: 'traEditor/setConcordanceProp',
		payload: {
			...searchOverrides,
			isLoading: true,
			results: [],
			activeResult: null,
			requestKey,
		},
	});

	const pObj = {
		term,
		matchType,
		searchTo,
		searchAllTMs,
		projectID,
		taskID,
	};

	gtag.event({
		category: 'Editor Actions',
		action: 'Search Concordance',
		label: pObj?.term ?? '',
	});

	ModelConcordance.search(pObj)
		.then((r) => {
			if (getState()?.traEditor?.concordance?.requestKey !== requestKey) {
				return;
			}

			if (Array.isArray(r)) {
				dispatch({
					type: 'traEditor/setConcordanceProp',
					payload: {
						isLoading: false,
						results: r,
						activeResult: r[0],
					},
				});
				return;
			}

			dispatch({
				type: 'traEditor/setConcordanceProp',
				payload: {
					isLoading: false,
					results: [],
					activeResult: null,
				},
			});
		})
		.catch((err) => {
			console.log(err);
			if (getState()?.traEditor?.concordance?.requestKey !== requestKey) {
				return;
			}

			Notifications.error('Something went wrong. Please try again!');
			dispatch({
				type: 'traEditor/setConcordanceProp',
				payload: {
					isLoading: false,
					results: [],
					activeResult: null,
				},
			});
		});
};

export const setConcordanceProps = (props) => (dispatch) => {
	dispatch({
		type: 'traEditor/setConcordanceProp',
		payload: {
			...props,
		},
	});
};

export const undo = () => async (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};

	const activeSegment = state?.activeSegment ?? null;
	const taskID = state?.task?.id;

	if (!activeSegment) return;

	const segmentTaskId = activeSegment?.task ? activeSegment.task : taskID;

	try {
		const res = await ModelSegments.undo(segmentTaskId, activeSegment.id);

		if (res?.changed) {
			dispatch({
				type: 'traEditor/updateSegmentText',
				payload: {
					segID: activeSegment.id,
					translationText: res?.changed?.translationText,
				},
			});

			dispatch({
				type: 'traEditor/setActiveHistory',
				payload: res?.history,
			});

			// textEditor2.jsx's `[value]` effect used to skip this row while
			// it had focus, which is why this used to also push the content
			// into Quill directly here. That focus check was removed there
			// (see "No focus check here" in setEditorContents()'s effect) so
			// external edits while focused always reach the visible editor -
			// doing it a second time here as well raced that effect's own
			// clipboard.convert()/setContents() with this simpler one, and
			// whichever ran last silently won, dropping tag blots the other
			// conversion handled correctly. Relying on the single effect now.
			setTimeout(async () => {
				const qlEditor = document.querySelector(
					`#editorRow_${activeSegment.id} .ql-editor`
				);
				// setEditorContents() (triggered by the translationText update
				// above flowing into this row's `value` prop) already restores
				// a selection through Quill's own setSelection() API, keeping
				// Quill's internal cursor model and the real DOM caret in sync.
				// placeCaretAtEnd() moves the *native* browser selection
				// directly, bypassing that API entirely - so it was racing the
				// Quill-driven restore, and whichever ran last would leave
				// Quill's internal index pointing somewhere other than where
				// the caret actually was. Typing then diffed against that
				// stale index, and landing on a block boundary could get
				// synthesized as a new block/line instead of inline text -
				// reproduced as a literal "\n" ending up in the saved
				// translationText after undo. Going through Quill's own API
				// keeps a single source of truth for the cursor instead.
				// container.__quill was an undocumented Quill 1.x internal;
				// Quill 2.x tracks instances in its own internal WeakMap
				// instead, exposed only via the public static Quill.find().
				const Quill = await getQuill();
				const quillInstance = Quill?.find(
					qlEditor?.closest('.ql-container')
				);
				if (quillInstance) {
					quillInstance.focus();
					quillInstance.setSelection(quillInstance.getLength(), 0);
				} else if (qlEditor) {
					qlEditor.focus();
					placeCaretAtEnd(qlEditor);
				}
			}, 50);
		}
	} catch (err) {
		console.error(err);
	}
};

export const redo = () => async (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};

	const activeSegment = state?.activeSegment ?? null;
	const taskID = state?.task?.id;

	if (!activeSegment) return;

	try {
		const res = await ModelSegments.redo(taskID, activeSegment.id);

		if (res?.changed) {
			dispatch({
				type: 'traEditor/updateSegmentText',
				payload: {
					segID: activeSegment.id,
					translationText: res?.changed?.translationText,
				},
			});

			dispatch({
				type: 'traEditor/setActiveHistory',
				payload: res?.history,
			});

			// See the matching comment in undo() above - this duplicate direct
			// Quill update is no longer needed now that textEditor2.jsx's
			// `[value]` effect applies external edits regardless of focus, and
			// racing it here was dropping tag blots on some undo/redo passes.
			setTimeout(async () => {
				const qlEditor = document.querySelector(
					`#editorRow_${activeSegment.id} .ql-editor`
				);
				// setEditorContents() (triggered by the translationText update
				// above flowing into this row's `value` prop) already restores
				// a selection through Quill's own setSelection() API, keeping
				// Quill's internal cursor model and the real DOM caret in sync.
				// placeCaretAtEnd() moves the *native* browser selection
				// directly, bypassing that API entirely - so it was racing the
				// Quill-driven restore, and whichever ran last would leave
				// Quill's internal index pointing somewhere other than where
				// the caret actually was. Typing then diffed against that
				// stale index, and landing on a block boundary could get
				// synthesized as a new block/line instead of inline text -
				// reproduced as a literal "\n" ending up in the saved
				// translationText after undo. Going through Quill's own API
				// keeps a single source of truth for the cursor instead.
				// container.__quill was an undocumented Quill 1.x internal;
				// Quill 2.x tracks instances in its own internal WeakMap
				// instead, exposed only via the public static Quill.find().
				const Quill = await getQuill();
				const quillInstance = Quill?.find(
					qlEditor?.closest('.ql-container')
				);
				if (quillInstance) {
					quillInstance.focus();
					quillInstance.setSelection(quillInstance.getLength(), 0);
				} else if (qlEditor) {
					qlEditor.focus();
					placeCaretAtEnd(qlEditor);
				}
			}, 50);
		}
	} catch (err) {
		console.error(err);
	}
};

export const updateFromSuggestion = (suggestion) => (dispatch, getState) => {
	const activeSegment = getState()?.traEditor?.activeSegment;

	if (!activeSegment) return;

	if (!suggestion) return;

	let translationText = suggestion?.trg;

	if (suggestion?.type == 'TB') {
		insertTextAtCursor(suggestion?.trg, suggestion?.trg?.length);
		return;
	}

	let matchType = '';
	let matchScore = '';
	let matchID = '';

	if (suggestion?.type == 'MT') {
		matchType = 'MT';
		matchID = suggestion?.metas?.id;
		matchScore = suggestion?.metas?.abbr;
	}

	if (suggestion?.type == 'TM') {
		matchType = 'TM';
		matchScore = suggestion?.score;
		matchID = suggestion?.metas?.id;
	}

	const matchSourceTaskId =
		suggestion?.type === 'TM' ? getSuggestionSourceTaskID(suggestion) : null;

	dispatch({
		type: 'traEditor/updateSegment',
		payload: {
			id: activeSegment?.id,
			status: 'draft',
			nextStatus: 'draft',
			matchType,
			matchScore,
			translationText,
			matchID,
			matchSourceTaskId,
			forSave: true,
		},
	});

	dispatch({ type: 'traEditor/reRender' });

	nextTick(async () => {
		dispatch(saveAll());

		if (suggestion?.type == 'TM') {
			await ModelTranslationMemories.incrementEntryHits({
				entryID: suggestion?.metas?.id,
			});
		}
	});
};

export const updateFromSuggestionIndex = (index) => (dispatch, getState) => {
	const activeSegment = getState()?.traEditor?.activeSegment;
	const suggestions = getState()?.traEditor?.suggestions;
	const user = getState()?.user;
	const topRightBarIndex = getState()?.traEditor?.topRightBarIndex ?? 0;

	if (!activeSegment) return;

	if (!suggestions) return;
	const tbTabIndex = getState()?.traEditor?.topRightBarTabs?.tb ?? 1;
	const mode = user?.showTbInSeparateTab && topRightBarIndex === tbTabIndex ? 'tb' : 'tm';
	const array = getVisibleSuggestions(suggestions, user, mode);

	const suggestion = array[index];

	dispatch(updateFromSuggestion(suggestion));
};

export const enableEditSource = () => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};
	const segments = getSelectedSegments(state);
	if (segments.length == 0) return;

	const segment = segments[0];

	dispatch({
		type: 'traEditor/updateSegment',
		payload: {
			...segment,
			editSource: true,
			originalSourceText: segment.sourceText,
		},
	});
};

export const disableEditSource = () => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};
	const segments = getSelectedSegments(state);
	if (segments.length == 0) return;

	const segment = segments[0];

	segment.editSource = null;
	segment.sourceText = segment.originalSourceText ?? segment.sourceText;

	dispatch({ type: 'traEditor/updateSegment', payload: segment });
};

export const editSource =
	(segmentID, updateRepetitions = false) =>
	(dispatch, getState) => {
		const state = getState()?.traEditor ?? {};

		const segment = state.arSegments.getSegment(segmentID);

		if (!segment) return;

		segment.editSource = null;

		ModelSegments.updateSource({
			segmentID,
			source: segment.sourceText,
			updateRepetitions,
		})
			.then((response) => {
				// The server re-appends tags removed from the source
				if (response?.sourceText) segment.sourceText = response.sourceText;

				dispatch({ type: 'traEditor/updateSegment', payload: segment });

				if (updateRepetitions) {
					const arSegmentsToSave = state.arSegments
						.filter((S) => {
							return S.sameAs == segment?.id;
						})
						.map((S) => ({ ...S, sourceText: segment.sourceText }))
						.toList()
						.toJS();

					dispatch({
						type: 'traEditor/updateSegments',
						payload: arSegmentsToSave,
					});
				}
			})
			.catch((err) => console.log(err));
	};

export const restoreSourceVersion =
	(segmentID, index, updateRepetitions = false) =>
	(dispatch, getState) => {
		const state = getState()?.traEditor ?? {};

		const segment = state.arSegments.getSegment(segmentID);

		if (!segment) return Promise.resolve();

		return ModelSegments.restoreSource({ segmentID, index, updateRepetitions })
			.then((response) => {
				if (!response?.resp) return;

				segment.editSource = null;
				segment.sourceText = response.sourceText;

				dispatch({ type: 'traEditor/updateSegment', payload: segment });
			})
			.catch((err) => console.log(err));
	};

export const moveEditorToFirstAvailable = () => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};
	const taskOptions = getTaskOptions(getState());

	let firstAvailableIndex = 0;

	for (const sid of taskOptions) {
		if (
			state.arDisplaySegments.hasSegment(sid) &&
			state.arDisplaySegments.getSegment(sid).available
		) {
			break;
		}

		firstAvailableIndex++;
	}

	// Hack to scroll to first segment
	if (firstAvailableIndex === 0) {
		dispatch(moveEditorToLine(1, 'end'));

		setTimeout(() => {
			dispatch(moveUp());
		}, 500);

		return;
	}

	dispatch(moveEditorToLine(firstAvailableIndex));
};

export const moveEditorToLastAvailable = () => (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};
	const taskOptions = getTaskOptions(getState());

	let lastAvailableSegment;

	for (let i = taskOptions.length - 1; i >= 0; i--) {
		if (
			!state.arDisplaySegments.hasSegment(taskOptions[i]) ||
			!state.arDisplaySegments.getSegment(taskOptions[i]).available
		) {
			continue;
		}

		lastAvailableSegment = state.arDisplaySegments.getSegment(taskOptions[i]);
		break;
	}

	if (lastAvailableSegment != null) {
		dispatch(moveEditorToLine(lastAvailableSegment.ordr - 1));
	}
};

export const confirmAllGiven =
	(segments, updateTM = null, task = false) =>
	(dispatch) => {
		if (segments.length == 0) return;

		let segmentsToConfirm = [];

		// In case, arSegments or arDisplaySegments are passed instead of an array of segments
		if (typeof segments === 'object') {
			Object.keys(segments).map((k) => {
				const segment = segments[k];

				segmentsToConfirm.push(segment);
			});
		}

		// No object was passed, use array
		if (segmentsToConfirm.length === 0) {
			segmentsToConfirm = segments;
		}

		segmentsToConfirm.forEach((S) => {
			if (!segmentIsEditable(S) || S.status == SEGMENT_STATUS.CONFIRMED) return;

			S.hasChanges = false;
			S.forSave = true;
			S.status = 'confirmed';
			S.nextStatus = 'confirmed';
			S.updateTM = updateTM;

			dispatch({ type: 'traEditor/updateSegment', payload: S });
		});

		gtag.event({
			category: 'Editor Actions',
			action: 'Confirm',
			label: '',
		});

		nextTick(() => dispatch(saveAll(task)));
	};

export const confirmAll = (updateTM = null) => {
	return (dispatch, getState) => {
		dispatch(
			confirmAllGiven(
				getState().traEditor?.arSegments.toObject() ?? {},
				updateTM
			)
		);
	};
};

export const unConfirmAllGiven =
	(segments, updateTM = null) =>
	(dispatch) => {
		if (segments.length == 0) return;

		let segmentsToUnConfirm = [];

		// In case, arSegments or arDisplaySegments are passed instead of an array of segments
		if (typeof segments === 'object') {
			Object.keys(segments).map((k) => {
				const segment = segments[k];

				segmentsToUnConfirm.push(segment);
			});
		}

		// No object was passed, use array
		if (segmentsToUnConfirm.length === 0) {
			segmentsToUnConfirm = segments;
		}

		segmentsToUnConfirm.forEach((S) => {
			if (!segmentIsEditable(S) || S.status == SEGMENT_STATUS.DRAFT) return;

			S.hasChanges = false;
			S.forSave = true;
			S.status = SEGMENT_STATUS.DRAFT;
			S.nextStatus = SEGMENT_STATUS.DRAFT;
			S.updateTM = updateTM;

			dispatch({ type: 'traEditor/updateSegment', payload: S });
		});

		gtag.event({
			category: 'Editor Actions',
			action: 'Un-Confirm',
			label: '',
		});

		nextTick(() => {
			dispatch(saveAll());
		});
	};

export const unConfirmAll = (updateTM = null) => {
	return (dispatch, getState) => {
		dispatch(
			unConfirmAllGiven(
				getState().traEditor?.arSegments.toObject() ?? {},
				updateTM
			)
		);
	};
};

const findSegmentsPositionInArray = (sid, taskSegemntsOrder) => {
	return taskSegemntsOrder.indexOf(parseInt(sid));
};

const validateSegmentsSelection = (arSelectedSegments, taskSegemntsOrder) => {
	arSelectedSegments = arSelectedSegments.sort(
		({ ordr: a }, { ordr: b }) => a - b
	);

	//chk locked
	for (let idx in arSelectedSegments) {
		const S = arSelectedSegments[idx];

		//is locked
		if (S.isLocked || S.isRevisionLocked) return false;

		//is rep
		if (S.isRepetition && !S.isInitRepetition) return false;

		//is translated in next step

		//are in order
		if (idx == 0) continue;

		const thisSegmentIndex = findSegmentsPositionInArray(
			S.id,
			taskSegemntsOrder
		);
		const prevSegmentIndex = findSegmentsPositionInArray(
			arSelectedSegments[idx - 1].id,
			taskSegemntsOrder
		);

		if (Math.abs(thisSegmentIndex - prevSegmentIndex) != 1) return false;
	}

	//reach here everything is valid
	return true;
};

export const joinSelectedSegments = () => async (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};


	//chk if segmets contains rep or is locked
	//chk if segments are in order

	const arSelectedSegments = state.arSegments
		.filter(({ checked }) => checked)
		.toList()
		.toJS();

	if (arSelectedSegments.length < 2) {
		Notifications.warning(`Please select at least 2 segments.`);
		return;
	}

	// For a split subtask, options is keyed by the parent task's id, not
	// this subtask's own id (see getTaskOptions() above) - falling back to
	// state.task.id alone left taskSegemntsOrder empty for any split task,
	// so every join attempt failed validation with "cannot be joined".
	const taskSegemntsOrder =
		state?.task?.options?.[state.task.id] ??
		state?.task?.options?.[state.task.parent] ??
		[];

	//validate join segments
	const isValidSelection = validateSegmentsSelection(
		arSelectedSegments,
		taskSegemntsOrder
	);
	if (!isValidSelection) {
		Notifications.warning(`These segments cannot be joined`);
		return;
	}

	if (new Set(arSelectedSegments.map((segment) => String(segment.task))).size > 1) {
        Notifications.warning('Segments to join must belong to one task.');
        return;
    }
    const taskID = arSelectedSegments[0].task ?? state.task.id;

    try {
        //keep only ids for join
		//in proper order
		const arSortedSelectedSegments = arSelectedSegments.sort((a, b) => {
			if (a.ordr > b.ordr) return 1;
			if (a.ordr < b.ordr) return -1;
			return 0;
		});

		const arSegmentIdsToJoin = arSortedSelectedSegments.map((S) => S.id);
		const res = await ModelSegments.joinSegments(arSegmentIdsToJoin, taskID);

		//new id is returned
		if (!res?.resp && res.err) {
			Notifications.warning(res.err);
			return;
		}

		if (!res?.resp) {
			Notifications.warning('Some error occurred. Please try again.');
			return;
		}

		batch(() => {
			if (state.taskids?.length > 1) {
                const joinedTasks = state.joinedTasks.map((item) => String(item.id) === String(res.task.id) ? res.task : item);
                const joinedSegmentIds = [...new Set((state.joinedTaskFocus ? [res.task] : joinedTasks).flatMap(getTaskSegmentIds))];
                dispatch({ type: 'traEditor/setProp', payload: { joinedTasks, joinedSegmentIds, task: { ...res.task, options: { [res.task.id]: joinedSegmentIds } } } });
                dispatch(getSegments());
            } else {
                dispatch({ type: 'traEditor/updateTask', payload: res.task });
                dispatch({ type: 'traEditor/initSegments', payload: res.segments });
				dispatch({ type: 'traEditor/filterAllSegments' });
				dispatch(setSelectedSegmentById(res.id));
			}
		});

		// The old rows are removed by the merge. Resolve the replacement by ID
		// against the refreshed, filtered order before scrolling and focusing it.
		nextTick(() => dispatch(gotoSegmentById(res.id, true, 'auto', 'end')));
	} catch (err) {
		console.log(err);
	}
};

export const splitSegment = () => async (dispatch, getState) => {
	const state = getState()?.traEditor ?? {};

	const srcEditor = document?.activeElement;
	if (!srcEditor) return;

	if (!srcEditor.closest('.src')) {
		return;
	}

	//can be splitted between tags
	const splitPos = getSourceCaretPosition(srcEditor);
	const canSplit = canSplitSourceAtPosition(srcEditor, splitPos);

	if (!canSplit) {
		Notifications.warning(
			'You cannot split between G tags. Try before or after the tags.'
		);
		return;
	}

	const gridRow = srcEditor.closest('.gridRow');
	if (!gridRow) return;

	const segmentID = gridRow.getAttribute('data-segid');
	const taskID = state.arSegments?.getSegment(segmentID)?.task ?? state.task.id;

	const _thisSegment = state.arSegments?.getSegment(segmentID) ?? null;
	if (!_thisSegment) return;

	//cases that cannot split segment
	if (
		(_thisSegment.isRepetition && !_thisSegment.isInitRepetition) ||
		_thisSegment.isLocked ||
		_thisSegment.isRevisionLocked
	) {
		Notifications.warning('Secondary repetitions or locked segments cannot be split.');
		return;
	}

	try {
		const res = await ModelSegments.splitSegment(splitPos, segmentID, taskID);

		//new id is returned
		if (!res?.resp && res.err) {
			Notifications.warning(res.err);
			return;
		}

		if (!res?.resp) {
			Notifications.warning('Some error occurred. Please try again.');
			return;
		}

		batch(() => {
			if (state.taskids?.length > 1) {
                const joinedTasks = state.joinedTasks.map((item) => String(item.id) === String(res.task.id) ? res.task : item);
                const joinedSegmentIds = [...new Set((state.joinedTaskFocus ? [res.task] : joinedTasks).flatMap(getTaskSegmentIds))];
                dispatch({ type: 'traEditor/setProp', payload: { joinedTasks, joinedSegmentIds, task: { ...res.task, options: { [res.task.id]: joinedSegmentIds } } } });
                dispatch(getSegments());
            } else {
                dispatch({ type: 'traEditor/updateTask', payload: res.task });
                dispatch({ type: 'traEditor/initSegments', payload: res.segments });
				dispatch({ type: 'traEditor/filterAllSegments' });
				dispatch(setSelectedSegmentById(res.id));
			}
		});

		// The first new part retains the original translation.
		nextTick(() => dispatch(gotoSegmentById(res.id, true, 'auto', 'end')));
	} catch (err) {
		console.log(err);
	}
};

export const runAiQualityTask = ({ segmentOnly = false } = {}) => async (dispatch, getState) => {
    if (getState().traEditor.aiQualityRunning) return;
    const { task, taskids, viewID, activeSegment, arSegments } = getState().traEditor;
    const segment = segmentOnly && activeSegment?.id ? arSegments?.getSegment(activeSegment.id) : null;
    if (segmentOnly && !segment?.id) return;
    const ids = segmentOnly ? [segment.task || task.id] : (taskids?.length ? taskids : [task.id]);
    dispatch({ type: 'traEditor/setProp', payload: { aiQualityRunning: true } });
    try {
        await runAiQuality(ids, {
            viewID,
            ...(segmentOnly ? { segmentID: segment.id } : {}),
            onResults: (taskID, results) => {
                const state = getState().traEditor;
                if (state.task?.id !== task.id || state.viewID !== viewID) return;
                const values = { ...(state.aiQualityResults || {}) };
                Object.entries(results || {}).forEach(([id, result]) => {
                    values[id] = result;
                });
                dispatch({ type: 'traEditor/setProp', payload: { aiQualityResults: values } });
            }
        });
    } finally {
        dispatch({ type: 'traEditor/setProp', payload: { aiQualityRunning: false } });
    }
};
